/**
 * Tag Manager — Popover for creating, editing, and deleting tags.
 * Ported from PaaS TagManager.tsx (193 lines)
 *
 * Drawn in two places, one component: the article editor's Tags panel (an
 * icon beside the tag chips) and the Library Documents page header, beside
 * "Types" (`trigger="button"`). The page is where a person who may write tags
 * but not articles reaches it (LB-6 run 1 live proof: the editor opens only
 * with write:wiki, while the tag routes declare write:library.documents).
 */
import { useState } from 'react';
import { Settings, Plus, Pencil, Trash2, Check, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from 'sonner';
import { useHasPermission } from '@/lib/use-has-permission';

const PRESET_COLORS = [
  '#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
];

export interface KBTag {
  id: string;
  name: string;
  color: string;
}

/**
 * Save the add / edit form. A new tag is POST /wiki/tags. An edit is ONE
 * PATCH /wiki/tags/:id, which renames (and recolours) the tag in place: it
 * keeps its id and stays on every document it was on. The edit was a DELETE
 * then a POST — the delete took the tag off every document and the new tag
 * was on none (LB-6 run 1, reproduced on PostgreSQL). Exported for its test.
 */
export async function saveTag(
  editingTagId: string | null,
  form: { name: string; color: string },
): Promise<void> {
  const body = { name: form.name.trim(), color: form.color };
  if (editingTagId) await apiClient.patch(`/wiki/tags/${editingTagId}`, body);
  else await apiClient.post('/wiki/tags', body);
}

interface TagManagerProps {
  tags: KBTag[];
  onTagsChange: () => void;
  documentCounts: Record<string, number>;
  /**
   * How the popover is opened: 'icon' (the editor's Tags panel) or 'button'
   * (a "Tags" button drawn like the Documents page's "Types" beside it).
   */
  trigger?: 'icon' | 'button';
}

export function TagManager({ tags, onTagsChange, documentCounts, trigger = 'icon' }: TagManagerProps) {
  // Library LB6-T3 / L6-04 — creating, renaming and deleting a tag is POST /
  // DELETE /wiki/tags, which declares write:library.documents (decision 26:
  // tag writes cost the document write key). The manager is drawn only for
  // a person who holds it.
  const canManageTags = useHasPermission('write', 'library.documents');
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', color: PRESET_COLORS[0] });

  const handleStartAdd = () => {
    setFormData({ name: '', color: PRESET_COLORS[0] });
    setIsAdding(true);
    setEditingTagId(null);
  };

  const handleStartEdit = (tag: KBTag) => {
    setFormData({ name: tag.name, color: tag.color || PRESET_COLORS[0] });
    setEditingTagId(tag.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingTagId(null);
    setFormData({ name: '', color: PRESET_COLORS[0] });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    try {
      await saveTag(editingTagId, formData);
      toast.success(`Tag "${formData.name.trim()}" ${editingTagId ? 'updated' : 'created'}`);
      handleCancel();
      onTagsChange();
    } catch (err: unknown) {
      // The server says why (a name another tag already has, an empty name).
      toast.error(getApiErrorMessage(err, 'Failed to save tag'));
    }
  };

  const handleDelete = async (tagId: string) => {
    try {
      await apiClient.delete(`/wiki/tags/${tagId}`);
      toast.success('Tag deleted');
      onTagsChange();
    } catch {
      toast.error('Failed to delete tag');
    }
  };

  if (!canManageTags) return null;
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {trigger === 'button' ? (
          <Button variant="outline" size="sm" className="gap-1 text-xs" title="Manage tags">
            <Tag className="h-3 w-3" /> Tags
          </Button>
        ) : (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" aria-label="Manage tags" title="Manage tags">
            <Settings className="h-4 w-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align={trigger === 'button' ? 'end' : 'start'}>
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h4 className="font-semibold text-sm">Manage Tags</h4>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1 text-primary hover:text-primary hover:bg-primary/10"
            onClick={handleStartAdd}
          >
            <Plus className="h-3 w-3" /> Add Tag
          </Button>
        </div>

        {/* Add/Edit Form */}
        {(isAdding || editingTagId) && (
          <div className="p-4 bg-muted/30 border-b space-y-4 animate-in slide-in-from-top-2 duration-200">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase text-muted-foreground">Name</label>
              <Input
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Tag name..."
                className="h-8 text-sm"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase text-muted-foreground">Color</label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map(color => (
                  <button
                    key={color}
                    className={cn(
                      'w-6 h-6 rounded-full transition-transform hover:scale-110',
                      formData.color === color && 'ring-2 ring-primary ring-offset-2 scale-110'
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={handleCancel}>Cancel</Button>
              <Button size="sm" className="h-8 text-xs" onClick={handleSave}>
                <Check className="h-3 w-3 mr-1" /> {editingTagId ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        )}

        {/* Tag List */}
        <div className="p-1 max-h-[300px] overflow-auto">
          {tags.map(tag => (
            <div key={tag.id} className="group flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: tag.color || '#ccc' }} />
                <span className="text-sm font-medium">{tag.name}</span>
                <span className="text-xs text-muted-foreground">({documentCounts[tag.id] || 0})</span>
              </div>
              <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => handleStartEdit(tag)} aria-label={`Edit tag '${tag.name}'`} title="Edit tag">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDelete(tag.id)} aria-label={`Delete tag '${tag.name}'`} title="Delete tag">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {tags.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-4">No tags yet. Click "+ Add Tag" to create one.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
